import { gql, useQuery } from '@apollo/client'
import { Separator, Spinner } from '@farol-ds/react'
import { nullableConnectionNodes } from '@jusbrasil-web/shared-apollo-client/utilities'
import React, { useEffect, useMemo, useState } from 'react'

import { Comment } from '../../comment/comment'
import {
  CommentList_DocumentFragment,
  CommentList_ViewerFragment,
  LoadMoreCommentsByDocumentQuery,
  LoadMoreCommentsByDocumentQueryVariables,
} from './__generated__/commentList.graphql'
import styles from './styles.module.scss'

export interface CommentListProps {
  document: CommentList_DocumentFragment
  viewer: CommentList_ViewerFragment | null
  onLoadMore?: () => void
}

export const LOAD_MORE_COMMENTS = gql`
  query LoadMoreCommentsByDocument(
    $docId: NumericID!
    $artifact: String!
    $limit: Int!
    $after: String
  ) {
    root {
      documentByNumericID(artifact: $artifact, docId: $docId) {
        id
        comments(first: $limit, after: $after) {
          edges {
            cursor
            node {
              ...Comment_comment
            }
          }
        }
      }
    }
  }

  ${Comment.fragments.comment}
`

export function CommentList(props: CommentListProps) {
  const { document, viewer, onLoadMore } = props
  const { docId, artifact } = document
  const { data, fetchMore, loading } = useQuery<
    LoadMoreCommentsByDocumentQuery,
    LoadMoreCommentsByDocumentQueryVariables
  >(LOAD_MORE_COMMENTS, {
    notifyOnNetworkStatusChange: true,
    variables: {
      docId,
      artifact,
      after: null,
      limit: 4,
    },
  })

  const edges = data?.root.documentByNumericID?.comments?.edges

  const comments = useMemo(() => {
    const nodes = nullableConnectionNodes(data?.root.documentByNumericID?.comments)
    const seen = new Set<string>()
    return nodes.filter((node) => {
      if (seen.has(node.id)) return false
      seen.add(node.id)
      return true
    })
  }, [data?.root.documentByNumericID?.comments])

  const lastCursor = useMemo(() => {
    if (!edges?.length) return null
    for (let i = edges.length - 1; i >= 0; i--) {
      const cursor = edges[i]?.cursor
      if (cursor) return cursor
    }
    return null
  }, [edges])

  const [lastFetchedCursor, setLastFetchedCursor] = useState<string | null>(null)
  const [lastItem, setLastItem] = useState<Element | null>(null)
  const lastCommentIndex = comments.length - 1

  useEffect(() => {
    if (!lastItem || !lastCursor || lastCursor === lastFetchedCursor) return

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        fetchMore({
          variables: { after: lastCursor },
        }).then(() => {
          setLastFetchedCursor(lastCursor)
          onLoadMore?.()
        })
      }
    })

    observer.observe(lastItem)
    return () => observer.unobserve(lastItem)
  }, [lastItem, lastCursor, lastFetchedCursor, fetchMore, onLoadMore])

  return (
    <div className={styles.commentList}>
      {comments.map((comment, idx) => (
        <React.Fragment key={comment.id}>
          <div ref={lastCommentIndex === idx ? setLastItem : undefined}>
            <Comment comment={comment} document={document} viewer={viewer} />
          </div>
          {idx < comments.length - 1 && <Separator />}
        </React.Fragment>
      ))}
      {loading && <Spinner className={styles.commentListLoading} />}
    </div>
  )
}

CommentList.fragments = {
  document: gql`
    fragment CommentList_document on Document {
      docId
      artifact
      numComments
      ...Comment_document
    }
    ${Comment.fragments.document}
  `,
  viewer: gql`
    fragment CommentList_viewer on Profile {
      ...Comment_viewer
    }
    ${Comment.fragments.viewer}
  `,
}
